/**
 * Browser-side OAuth 2.1 (Authorization Code + PKCE) client for Kobiton.
 *
 * Kobiton's production authorization server advertises its endpoints at
 * https://api.kobiton.com/.well-known/oauth-authorization-server:
 *   - authorization_endpoint: /oauth/authorize
 *   - token_endpoint:         /oauth/token
 *   - registration_endpoint:  /oauth/register  (Dynamic Client Registration)
 *   - PKCE:  S256 required
 *   - token_endpoint_auth_methods: "none"  (public client — no client secret)
 *   - scope: "mcp"
 *
 * Other environments use the same endpoint paths. The token + register
 * endpoints send permissive CORS headers, so the whole flow runs client-side
 * with no backend.
 */

const SCOPE = 'mcp';

import { getActiveAccount, saveAccount } from './accounts';
import { DEFAULT_API_URL, normalizeApiUrl } from './environment';

const CLIENT_ID_KEY = 'kobiton.client_id';
const CODE_VERIFIER_KEY = 'kobiton.pkce_code_verifier';
const STATE_KEY = 'kobiton.oauth_state';
const API_URL_KEY = 'kobiton.oauth_api_url';

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
  [key: string]: unknown;
}

/** The redirect URI must exactly match what we register — derive it from the live origin. */
function redirectUri(): string {
  return `${window.location.origin}/callback.html`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256(input: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return new Uint8Array(digest);
}

/**
 * Obtain a client_id via Dynamic Client Registration. The result is cached in
 * localStorage keyed by API environment and redirect URI, so we register at
 * most once per environment/origin pair.
 */
async function getClientId(baseApiUrl: string): Promise<string> {
  const cacheKey = `${CLIENT_ID_KEY}:${baseApiUrl}:${redirectUri()}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    return cached;
  }

  const response = await fetch(`${baseApiUrl}/oauth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_name: 'quickportal',
      redirect_uris: [redirectUri()],
      grant_types: ['authorization_code', 'refresh_token'],
      response_types: ['code'],
      token_endpoint_auth_method: 'none',
      scope: SCOPE,
    }),
  });

  if (!response.ok) {
    throw new Error(`Dynamic client registration failed: ${response.status} ${await response.text()}`);
  }

  const data = (await response.json()) as { client_id?: string };
  if (!data.client_id) {
    throw new Error('Registration response did not include a client_id.');
  }

  localStorage.setItem(cacheKey, data.client_id);
  return data.client_id;
}

/**
 * Kick off the login: generate PKCE material, stash it in the window that will
 * receive the callback, and navigate that window to Kobiton's authorization
 * endpoint. Passing a popup keeps the main app in place during sign-in.
 */
export async function beginLogin(apiUrl = DEFAULT_API_URL, loginWindow: Window = window): Promise<void> {
  const baseApiUrl = normalizeApiUrl(apiUrl);
  const clientId = await getClientId(baseApiUrl);
  const codeVerifier = randomToken(48);
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier));
  const state = randomToken(16);

  loginWindow.sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
  loginWindow.sessionStorage.setItem(STATE_KEY, state);
  loginWindow.sessionStorage.setItem(API_URL_KEY, baseApiUrl);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri(),
    scope: SCOPE,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  loginWindow.location.assign(`${baseApiUrl}/oauth/authorize?${params.toString()}`);
}

/**
 * Complete the login on the callback page: validate state, exchange the
 * authorization code for tokens, persist them, and return the token response.
 */
export async function completeLogin(): Promise<TokenResponse> {
  const params = new URLSearchParams(window.location.search);

  const error = params.get('error');
  if (error) {
    throw new Error(`Authorization failed: ${error} — ${params.get('error_description') ?? 'no description'}`);
  }

  const code = params.get('code');
  const state = params.get('state');
  if (!code) {
    throw new Error('No authorization code was returned in the callback.');
  }

  const expectedState = sessionStorage.getItem(STATE_KEY);
  if (!state || state !== expectedState) {
    throw new Error('State mismatch — the callback may have been tampered with (CSRF).');
  }

  const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
  if (!codeVerifier) {
    throw new Error('Missing PKCE code verifier — start the login again.');
  }

  const baseApiUrl = normalizeApiUrl(sessionStorage.getItem(API_URL_KEY));
  const clientId = await getClientId(baseApiUrl);
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: clientId,
    code_verifier: codeVerifier,
  });

  const response = await fetch(`${baseApiUrl}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status} ${await response.text()}`);
  }

  const token = (await response.json()) as TokenResponse;

  // One-time PKCE material is spent; clear it.
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(API_URL_KEY);

  // Persist the login (token + derived username) and make it the active account.
  saveAccount(token, baseApiUrl);

  return token;
}

/** Read the active account's token (used by the barebones app page). */
export function getStoredToken(): TokenResponse | null {
  return getActiveAccount()?.token ?? null;
}
